import createCachedSelector from 're-reselect';

import { getCurrentLocationId } from 'selectors/session';

import { sortByNameCompareFromJob } from 'features/scheduleBuilder/ScheduleBuilderView/utils/sortByNameCompareFromJob';
import {
  selectJobAttribute,
  selectJobById,
} from 'features/scheduleBuilder/slices/jobs/slice';
import {
  selectAllTimeOffs,
  selectTimeOffAttribute,
  selectTimeOffRelationshipId,
} from 'features/scheduleBuilder/slices/timeOffs/slice';

import { CATEGORIES } from 'components/TimeOffForm/constants';

import { emptyArray } from '../utils/common';

import { selectIsTimeOffsVisible } from './index';

export const selectTimeOffDuration = createCachedSelector(
  (state, id) =>
    selectTimeOffAttribute({ state, id, attribute: 'dayDurations' }),
  dayDurations => dayDurations?.reduce((acc, curr) => curr?.duration + acc, 0)
)((_, id) => `selectTimeOffDuration-${id}`);

export const selectJobPtoCategories = createCachedSelector(
  (state, id) => selectJobAttribute({ state, id, attribute: 'ptoCategories' }),
  ptoCategories =>
    CATEGORIES.filter(category => ptoCategories?.includes(category.value))
)((_, id) => `time-off-job-categories-${id}`);

export const selectTimeOffsForJobOnDate = createCachedSelector(
  selectAllTimeOffs,
  (state, { jobId, date }) => ({ jobId, date }),
  (timeOffs, { jobId, date }) =>
    timeOffs.filter(timeOff => {
      const hasMatchingDate = timeOff.dayDurations?.some(
        dd => dd.date === date
      );
      const hasMatchingJob = timeOff.job?.id === jobId;
      return hasMatchingDate && hasMatchingJob;
    })
)((_, { jobId, date }) => `selectTimeOffsForJobOnDate-${jobId}-${date}`);

export const selectTimeOffIdsForJobOnDate = createCachedSelector(
  (state, props) => {
    const timeOffs = selectTimeOffsForJobOnDate(state, props);
    return timeOffs.length === 0 ? emptyArray : timeOffs;
  },
  timeOffs => timeOffs.map(({ id }) => id)
)((_, { jobId, date }) => `selectTimeOffIdsForJobOnDate-${jobId}-${date}`);

export const selectTimeOffsWholeDayForDate = createCachedSelector(
  (state, { date }) =>
    selectAllTimeOffs(state)
      .filter(timeOff => {
        if (!timeOff.isWholeDay) return false;
        if (!timeOff.dayDurations?.some(dd => dd.date === date)) return false;

        // Get the related job to check location
        const jobId = timeOff.job?.id;
        if (!jobId) return false;

        const job = selectJobById(state, jobId);
        const currentLocationId =
          getCurrentLocationId(state) ||
          state.getIn(['session'])?.currentLocation?.id;
        return job?.location?.id === String(currentLocationId);
      })
      .sort(
        sortByNameCompareFromJob({
          state,
          selectJobId: selectTimeOffRelationshipId,
        })
      )
      .map(({ id }) => id)
      .join(','),
  selectIsTimeOffsVisible,
  (ids, isTimeOffsVisible) =>
    isTimeOffsVisible && ids.length ? ids.split(',') : []
)((_, { date }) => `selectTimeOffsWholeDayForDate-${date}`);

export const selectTimeOffsPartialDayForDate = createCachedSelector(
  (state, { date }) =>
    selectAllTimeOffs(state)
      .filter(timeOff => {
        if (timeOff.isWholeDay) return false;
        if (!timeOff.dayDurations?.some(dd => dd.date === date)) return false;

        // Get the related job to check location
        const jobId = timeOff.job?.id;
        if (!jobId) return false;

        const job = selectJobById(state, jobId);
        const currentLocationId =
          getCurrentLocationId(state) ||
          state.getIn(['session'])?.currentLocation?.id;
        return job?.location?.id === String(currentLocationId);
      })
      .sort((a, b) => (new Date(a.startAt) < new Date(b.startAt) ? -1 : 1))
      .map(({ id }) => id)
      .join(','),
  selectIsTimeOffsVisible,
  (ids, isTimeOffsVisible) =>
    isTimeOffsVisible && ids.length ? ids.split(',') : []
)((_, { date }) => `selectTimeOffsPartialDayForDate-${date}`);
